import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { ArrowDown, ArrowUp, Check, GripVertical, MoveDiagonal, RotateCcw, Settings2, X } from 'lucide-react';
import { Button, EmptyState, IconButton, Tooltip, cn, useToast } from '@/components/ui';
import {
  HOME_COLUMNS,
  addHomeWidget,
  compactHomeLayout,
  homeRows,
  nextHomeSize,
  placeHomeWidget,
  previewHomeWidget,
  readingOrder,
  reorderHomeLayout,
  validateHomeLayout,
  type HomeCell,
  type HomePlacement,
  type HomeSize,
} from './homeLayout';
import {
  animateHomeRects,
  captureHomeRects,
  removeHomeVisual,
  homeMotionTokens,
  startHomePointer,
  type HomeRects,
  type HomePointerController,
} from './homeMotion';
import './home-grid.css';

export interface HomeGridApi {
  editing: boolean;
  canEdit: boolean;
  layout: readonly HomePlacement[];
  addWidget: (id: string, size: HomeSize, position?: HomeCell) => void;
  beginAddWidget: (id: string, size: HomeSize, event: ReactPointerEvent<HTMLElement>) => void;
}
export interface HomeWidgetControls extends HomeGridApi {
  grabbed: boolean;
  dragHandleProps: ButtonHTMLAttributes<HTMLButtonElement>;
  removeAction: ReactNode;
  dragHandleRef: (node: HTMLButtonElement | null) => void;
}
export interface HomeGridWidget {
  id: string;
  title: string;
  allowedSizes: readonly HomeSize[];
  content?: ReactNode;
  icon?: ReactNode;
  footer?: ReactNode;
  render?: (controls: HomeWidgetControls) => ReactNode;
}
export interface HomeDashboardGridProps {
  label: string;
  widgets: readonly HomeGridWidget[];
  layout: readonly HomePlacement[];
  onCommit: (next: HomePlacement[]) => void | Promise<void>;
  onTeamReset?: () => readonly HomePlacement[] | Promise<readonly HomePlacement[]>;
  readOnly?: boolean;
  compactUp?: boolean;
  hoverDelayMs?: 0 | 400 | 800 | 1500;
  toolbarExtra?: ReactNode | ((api: HomeGridApi) => ReactNode);
  onEditingChange?: (editing: boolean) => void;
}
interface Activity {
  id: string;
  kind: 'move' | 'resize' | 'keyboard' | 'add';
  base: HomePlacement[];
  preview: HomePlacement[];
  pointerId?: number;
  startX: number;
  startY: number;
  canvasTop: number;
  origin: HomePlacement;
  allowed: readonly HomeSize[];
  moved: boolean;
  inside: boolean;
  hoverDelayMs: 0 | 400 | 800 | 1500;
}
const query = '(min-width: 768px)';
function subscribe(listener: () => void) {
  const media = window.matchMedia(query);
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
}
const desktopSnapshot = () => window.matchMedia(query).matches;
const positionStyle = (item: HomePlacement): CSSProperties => ({
  left: 'calc(' + item.column + ' * (100% + var(--home-grid-gap)) / ' + HOME_COLUMNS + ')',
  width:
    'calc(' +
    item.columns +
    ' * (100% + var(--home-grid-gap)) / ' +
    HOME_COLUMNS +
    ' - var(--home-grid-gap))',
  top: 'calc(' + item.row + ' * (var(--home-grid-row) + var(--home-grid-gap)))',
  height: 'calc(' + item.rows + ' * var(--home-grid-row) + ' + (item.rows - 1) + ' * var(--home-grid-gap))',
});
const copy = (layout: readonly HomePlacement[]) => layout.map((item) => ({ ...item }));
const describe = (item: HomePlacement) =>
  'Колонка ' +
  (item.column + 1) +
  ', ряд ' +
  (item.row + 1) +
  ', размер ' +
  item.columns +
  ' на ' +
  item.rows +
  '.';
const sameLayout = (a: readonly HomePlacement[], b: readonly HomePlacement[]) =>
  a.length === b.length &&
  a.every((item, index) => {
    const other = b[index];
    return (
      item.id === other.id &&
      item.column === other.column &&
      item.row === other.row &&
      item.columns === other.columns &&
      item.rows === other.rows
    );
  });
function freeWidgetBody(event: ReactPointerEvent<HTMLElement>): boolean {
  const target = event.target;
  if (!(target instanceof Element) || event.defaultPrevented || !event.currentTarget.contains(target))
    return false;
  const blocker = target.closest(
    'button, a, input, textarea, select, option, label, summary, [controls], [draggable="true"], ' +
      '[contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"]), ' +
      '[role="button"], [role="link"], [role="checkbox"], [role="radio"], [role="switch"], ' +
      '[role="combobox"], [role="listbox"], [role="option"], [role="menuitem"], [role="tab"], ' +
      '[role="slider"], [role="spinbutton"], [role="scrollbar"], ' +
      '[data-home-edit-control], [data-home-drag-handle], [data-home-no-drag]',
  );
  if (blocker && event.currentTarget.contains(blocker)) return false;
  // Native scrollbar gutters belong to scrolling, even when their event target is the body.
  let element = target instanceof HTMLElement ? target : target.parentElement;
  while (element && event.currentTarget.contains(element)) {
    if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
      const rect = element.getBoundingClientRect(),
        left = rect.left + element.clientLeft,
        top = rect.top + element.clientTop;
      if (
        (element.scrollHeight > element.clientHeight &&
          (event.clientX < left || event.clientX >= left + element.clientWidth)) ||
        (element.scrollWidth > element.clientWidth &&
          (event.clientY < top || event.clientY >= top + element.clientHeight))
      )
        return false;
    }
    if (element === event.currentTarget) break;
    element = element.parentElement;
  }
  return true;
}
function HomeToolbarSlot({ slot, api }: { slot: HomeDashboardGridProps['toolbarExtra']; api: HomeGridApi }) {
  return <>{typeof slot === 'function' ? slot(api) : slot}</>;
}
const HomeRenderSlot = memo(
  function HomeRenderSlot({
    render,
    controls,
  }: {
    render: NonNullable<HomeGridWidget['render']>;
    controls: HomeWidgetControls;
    pointerKey: string | null;
  }) {
    return <>{render(controls)}</>;
  },
  (before, after) =>
    // Pointer feedback moves the host frame, not rich widget contents. Keep its start snapshot
    // until release, while still accepting live data/render and permission updates from the host.
    after.pointerKey !== null &&
    before.pointerKey === after.pointerKey &&
    before.render === after.render &&
    before.controls.editing === after.controls.editing &&
    before.controls.canEdit === after.controls.canEdit &&
    before.controls.grabbed === after.controls.grabbed &&
    before.controls.dragHandleProps.disabled === after.controls.dragHandleProps.disabled,
);
export function HomeDashboardGrid({
  label,
  widgets,
  layout,
  onCommit,
  onTeamReset,
  readOnly = false,
  compactUp = true,
  hoverDelayMs = 0,
  toolbarExtra,
  onEditingChange,
}: HomeDashboardGridProps) {
  const desktop = useSyncExternalStore(subscribe, desktopSnapshot, () => false),
    helpId = useId(),
    { toast } = useToast();
  const [draft, setDraft] = useState<HomePlacement[] | null>(null),
    [activity, setActivity] = useState<Activity | null>(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState(''),
    [announcement, setAnnouncement] = useState(''),
    [leaving, setLeaving] = useState(false);
  const canvas = useRef<HTMLDivElement>(null),
    handles = useRef(new Map<string, HTMLButtonElement>()),
    editButton = useRef<HTMLButtonElement>(null),
    doneButton = useRef<HTMLButtonElement>(null),
    root = useRef<HTMLElement>(null),
    session = useRef(0),
    latest = useRef({ draft, activity, pending, compactUp, readOnly }),
    firstRects = useRef<HomeRects | null>(null),
    pointer = useRef<HomePointerController | null>(null),
    placeholder = useRef<HTMLDivElement>(null),
    resizeFrame = useRef<HTMLDivElement>(null),
    sizeLabel = useRef<HTMLSpanElement>(null),
    liveStatus = useRef<HTMLParagraphElement>(null),
    modeTimer = useRef<number | undefined>(undefined);
  useLayoutEffect(() => {
    const previous = latest.current.activity,
      samePointer =
        activity &&
        previous &&
        activity.kind !== 'keyboard' &&
        activity.kind === previous.kind &&
        activity.id === previous.id &&
        activity.pointerId === previous.pointerId;
    // External data and permission renders must not replace the controller's newer preview.
    latest.current = {
      draft,
      activity: samePointer
        ? { ...activity, preview: previous.preview, moved: previous.moved, inside: previous.inside }
        : activity,
      pending,
      compactUp,
      readOnly,
    };
  }, [draft, activity, pending, compactUp, readOnly]);
  const stopModeExit = useCallback(() => {
    if (modeTimer.current !== undefined) window.clearTimeout(modeTimer.current);
    setLeaving(false);
  }, []);
  const leaveMode = useCallback(() => {
    if (!canvas.current) return;
    const config = homeMotionTokens(canvas.current);
    if (modeTimer.current !== undefined) window.clearTimeout(modeTimer.current);
    setLeaving(true);
    modeTimer.current = window.setTimeout(
      () => {
        setLeaving(false);
        modeTimer.current = undefined;
      },
      config.reduce ? config.reduced : config.mode,
    );
  }, []);
  useEffect(
    () => () => {
      if (modeTimer.current !== undefined) window.clearTimeout(modeTimer.current);
    },
    [],
  );
  const prepareMotion = useCallback(() => {
    firstRects.current = captureHomeRects(canvas.current);
  }, []);
  useLayoutEffect(() => {
    const before = firstRects.current;
    if (!before) return;
    firstRects.current = null;
    const active = latest.current.activity;
    animateHomeRects(canvas.current, before, active && active.kind !== 'keyboard' ? active.id : undefined);
    pointer.current?.refresh();
  });
  const editing = draft !== null,
    canEdit = !readOnly && !pending,
    current = draft ?? copy(layout),
    shown = activity?.preview ?? current;
  const sizes = Object.fromEntries(widgets.map((widget) => [widget.id, widget.allowedSizes]));
  const structural =
    validateHomeLayout(shown, sizes) ??
    (new Set(widgets.map((widget) => widget.id)).size !== widgets.length
      ? 'Идентификаторы виджетов повторяются.'
      : null);
  const announce = useCallback((message: string) => setAnnouncement(message), []);
  function enter() {
    if (!canEdit) return;
    session.current++;
    stopModeExit();
    setDraft(copy(layout));
    setError('');
    onEditingChange?.(true);
    announce('Настройка начата.');
  }
  function cancel() {
    if (pending) return;
    session.current++;
    prepareMotion();
    canvas.current?.querySelectorAll<HTMLElement>('[data-home-widget]').forEach((node) => {
      node.dataset.homeMotion = 'settling';
    });
    pointer.current?.dispose();
    pointer.current = null;
    setActivity(null);
    leaveMode();
    setDraft(null);
    setError('');
    onEditingChange?.(false);
    announce('Все изменения отменены.');
    requestAnimationFrame(() => editButton.current?.focus());
  }
  async function done() {
    if (!editing || !canEdit || activity) return;
    const next = copy(current);
    const issue = validateHomeLayout(next, sizes);
    if (issue) {
      setError(issue);
      return;
    }
    const focusWasInside = root.current?.contains(document.activeElement) ?? false;
    let restoreFocus = false;
    session.current++;
    setPending(true);
    setError('');
    try {
      await onCommit(next);
      session.current++;
      leaveMode();
      setDraft(null);
      onEditingChange?.(false);
      announce('Раскладка сохранена.');
      requestAnimationFrame(() => editButton.current?.focus());
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Раскладка не сохранена. Повтори.');
      restoreFocus = focusWasInside;
    } finally {
      setPending(false);
      if (restoreFocus)
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (!active || active === document.body || root.current?.contains(active))
            doneButton.current?.focus();
        });
    }
  }
  async function reset() {
    if (!canEdit || !onTeamReset) return;
    setPending(true);
    setError('');
    try {
      const next = copy(await onTeamReset()),
        issue = validateHomeLayout(next, sizes);
      if (issue) throw Error(issue);
      if (!editing) {
        stopModeExit();
        session.current++;
        onEditingChange?.(true);
      }
      prepareMotion();
      setActivity(null);
      setDraft(next);
      announce('Набор команды восстановлен в черновике. Нажми «Готово», чтобы сохранить.');
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Набор команды недоступен.');
    } finally {
      setPending(false);
    }
  }
  function addWidget(id: string, size: HomeSize, cell?: HomeCell) {
    if (!canEdit || latest.current.activity || latest.current.pending) return;
    try {
      let next = addHomeWidget(current, id, size);
      if (cell) next = placeHomeWidget(next, id, cell, compactUp);
      if (!editing) {
        stopModeExit();
        session.current++;
        onEditingChange?.(true);
      }
      prepareMotion();
      setDraft(next);
      setError('');
      announce('Виджет добавлен в черновик.');
      requestAnimationFrame(() => handles.current.get(id)?.focus());
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Не удалось добавить виджет.');
    }
  }
  function begin(id: string, kind: 'move' | 'resize', event: ReactPointerEvent<HTMLElement>) {
    if (!editing || !canEdit || !desktop || event.button !== 0 || activity) return;
    const item = current.find((item) => item.id === id),
      widget = widgets.find((widget) => widget.id === id),
      rect = canvas.current?.getBoundingClientRect();
    if (!item || !widget || !rect) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setActivity({
      id,
      kind,
      base: copy(current),
      preview: copy(current),
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      canvasTop: rect.top,
      origin: { ...item },
      allowed: widget.allowedSizes,
      moved: false,
      inside: true,
      hoverDelayMs,
    });
    announce(widget.title + ' выбран. Переноси по сетке.');
  }
  function beginAddWidget(id: string, size: HomeSize, event: ReactPointerEvent<HTMLElement>) {
    if (!canEdit || !desktop || event.button !== 0 || activity) return;
    try {
      const base = copy(current),
        seed = addHomeWidget(base, id, size),
        origin = seed.find((item) => item.id === id)!,
        rect = canvas.current?.getBoundingClientRect();
      if (!rect) return;
      if (!editing) {
        stopModeExit();
        session.current++;
        setDraft(base);
        onEditingChange?.(true);
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setActivity({
        id,
        kind: 'add',
        base,
        preview: seed,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        canvasTop: rect.top,
        origin,
        allowed: [size],
        moved: false,
        inside: false,
        hoverDelayMs,
      });
      announce('Перетащи виджет на сетку.');
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Не удалось добавить виджет.');
    }
  }

  const pointerKey =
    activity && activity.kind !== 'keyboard'
      ? activity.id + ':' + activity.kind + ':' + activity.pointerId
      : null;
  useLayoutEffect(() => {
    const start = latest.current.activity,
      area = canvas.current,
      compactAtStart = latest.current.compactUp;
    if (!pointerKey || !start || start.kind === 'keyboard' || start.pointerId === undefined || !area) return;
    const node = area.querySelector<HTMLElement>('[data-home-widget="' + CSS.escape(start.id) + '"]');
    if (!node) return;
    const controller = startHomePointer({
      canvas: area,
      node,
      placeholder: placeholder.current,
      resizeFrame: resizeFrame.current,
      sizeLabel: sizeLabel.current,
      layout: start.kind === 'add' ? addHomeWidget(start.base, start.id, start.origin) : start.base,
      start: { ...start, kind: start.kind, pointerId: start.pointerId },
      hoverDelayMs: start.hoverDelayMs,
      enabled: () =>
        area.isConnected &&
        node.isConnected &&
        !!latest.current.activity &&
        !latest.current.readOnly &&
        !latest.current.pending,
      onPreview: (target) => {
        const state = latest.current,
          active = state.activity;
        if (!active || active.id !== start.id) return { layout: start.preview, valid: false };
        // Keep an added ghost in the preview while restoring all existing neighbours outside the board.
        const seed = start.kind === 'add' ? addHomeWidget(start.base, start.id, start.origin) : start.base;
        let preview: HomePlacement[],
          valid = target !== null;
        try {
          preview = previewHomeWidget(seed, start.id, target, compactAtStart);
        } catch {
          preview = copy(seed);
          valid = false;
        }
        if (!sameLayout(active.preview, preview)) {
          const next = { ...active, preview, moved: true, inside: target !== null };
          latest.current = { ...state, activity: next };
        }
        const message = valid
          ? describe(preview.find((item) => item.id === start.id)!)
          : 'Место недоступно. При отпускании виджет вернётся.';
        if (liveStatus.current && liveStatus.current.textContent !== message)
          liveStatus.current.textContent = message;
        return { layout: preview, valid };
      },
      onFinish: (result) => {
        const state = latest.current,
          active = state.activity;
        if (!active || active.id !== start.id) return;
        let settled = copy(active.base),
          keep = result.keep;
        if (keep) {
          try {
            const seed =
              start.kind === 'add' ? addHomeWidget(start.base, start.id, start.origin) : start.base;
            settled = previewHomeWidget(seed, start.id, result.target, compactAtStart);
          } catch {
            keep = false;
          }
        }
        prepareMotion();
        if (firstRects.current?.has(start.id)) firstRects.current.get(start.id)!.box = result.from;
        latest.current = { ...state, activity: null, draft: settled };
        setDraft(settled);
        setActivity(null);
        const placed = settled.find((item) => item.id === start.id);
        announce(keep && placed ? 'Виджет поставлен. ' + describe(placed) : 'Перенос отменён.');
      },
    });
    pointer.current = controller;
    return () => {
      controller.dispose();
      if (pointer.current === controller) pointer.current = null;
    };
  }, [pointerKey, prepareMotion, announce]);
  function keyboard(id: string, event: KeyboardEvent<HTMLButtonElement>) {
    if (!editing || !canEdit) return;
    const item = current.find((item) => item.id === id),
      widget = widgets.find((widget) => widget.id === id);
    if (!item || !widget) return;
    if ((event.key === ' ' || event.code === 'Space') && !activity) {
      event.preventDefault();
      prepareMotion();
      setActivity({
        id,
        kind: 'keyboard',
        base: copy(current),
        preview: copy(current),
        origin: { ...item },
        allowed: widget.allowedSizes,
        startX: 0,
        startY: 0,
        canvasTop: 0,
        moved: false,
        inside: true,
        hoverDelayMs,
      });
      announce(
        widget.title +
          ' выбран. Стрелки — перенос, Shift и стрелки — размер, Enter — поставить, Escape — отменить.',
      );
      return;
    }
    if (activity?.id !== id || activity.kind !== 'keyboard') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      prepareMotion();
      setActivity(null);
      announce('Перенос отменён.');
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      prepareMotion();
      const settled = copy(activity.preview);
      setDraft(settled);
      setActivity(null);
      announce('Виджет поставлен. ' + describe(settled.find((item) => item.id === id)!));
      return;
    }
    const dx = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0,
      dy = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (!dx && !dy) return;
    event.preventDefault();
    const active = activity.preview.find((item) => item.id === id)!;
    let next: HomePlacement[];
    if (!desktop) {
      if (!dy || event.shiftKey) return;
      next = reorderHomeLayout(activity.preview, id, dy as -1 | 1);
    } else if (event.shiftKey) {
      const size = nextHomeSize(active, dx, dy, widget.allowedSizes);
      next = previewHomeWidget(activity.base, id, { ...active, ...size }, compactUp);
    } else
      next = previewHomeWidget(
        activity.base,
        id,
        { ...active, column: active.column + dx, row: active.row + dy },
        compactUp,
      );
    prepareMotion();
    setActivity({ ...activity, preview: next, moved: true });
    announce(describe(next.find((item) => item.id === id)!));
    requestAnimationFrame(() => handles.current.get(id)?.focus({ preventScroll: true }));
  }
  function remove(id: string) {
    if (!editing || !canEdit || activity) return;
    const item = current.find((item) => item.id === id);
    if (!item) return;
    const token = session.current;
    prepareMotion();
    removeHomeVisual(
      canvas.current,
      canvas.current?.querySelector<HTMLElement>('[data-home-widget="' + CSS.escape(id) + '"]') ?? null,
    );
    const next = current.filter((item) => item.id !== id);
    setDraft(compactUp ? compactHomeLayout(next) : copy(next));
    announce('Виджет убран. Можно вернуть.');
    requestAnimationFrame(() => {
      const remaining = readingOrder(next)[0];
      (remaining ? handles.current.get(remaining.id) : editButton.current)?.focus();
    });
    toast({
      title: 'Виджет убран',
      action: {
        label: 'Вернуть',
        undo: true,
        onClick: () => {
          const state = latest.current;
          if (session.current !== token || state.pending || state.readOnly || !state.draft) return;
          const base = state.activity?.base ?? state.draft;
          if (base.some((next) => next.id === id)) return;
          const restored = placeHomeWidget([...copy(base), { ...item }], id, item, state.compactUp);
          prepareMotion();
          canvas.current?.querySelectorAll<HTMLElement>('[data-home-widget]').forEach((node) => {
            node.dataset.homeMotion = 'settling';
          });
          pointer.current?.dispose();
          pointer.current = null;
          latest.current = { ...state, activity: null, draft: restored };
          setActivity(null);
          setDraft(restored);
          announce('Виджет возвращён.');
        },
      },
    });
  }
  const api: HomeGridApi = {
    editing,
    canEdit: canEdit && !activity,
    layout: shown,
    addWidget,
    beginAddWidget,
  };
  const ordered = readingOrder(shown),
    height = homeRows(shown) + (editing ? 2 : 0),
    focusable = editing && canEdit,
    showHandles = editing || leaving;
  return (
    <section
      ref={root}
      data-slot="home-dashboard-grid"
      data-home-editing={editing}
      data-home-mode-out={leaving || undefined}
      aria-label={label}
      className="space-y-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {!editing ? (
            <Button
              ref={editButton}
              variant="secondary"
              leftIcon={<Settings2 />}
              disabled={!canEdit}
              onClick={enter}
            >
              Настроить
            </Button>
          ) : (
            <>
              <Button
                ref={doneButton}
                leftIcon={<Check />}
                disabled={!canEdit || !!activity}
                onClick={() => void done()}
              >
                {pending ? 'Сохраняем…' : 'Готово'}
              </Button>
              <Button ref={editButton} variant="ghost" disabled={pending} onClick={cancel}>
                Отменить изменения
              </Button>
            </>
          )}
          {onTeamReset && (
            <Button
              variant="ghost"
              leftIcon={<RotateCcw />}
              disabled={!canEdit || !!activity}
              onClick={() => void reset()}
            >
              Сбросить к набору команды
            </Button>
          )}
        </div>
        <HomeToolbarSlot slot={toolbarExtra} api={api} />
      </div>
      <p id={helpId} className="sr-only">
        В режиме настройки: Пробел — взять, стрелки — перенести, Shift и стрелки — изменить размер, Enter —
        поставить, Escape — отменить. На узком экране доступны порядок и удаление.
      </p>
      <p ref={liveStatus} role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {editing && desktop && (
        <p className="text-xs text-fg-muted">
          Перетаскивай за свободную часть виджета. Зелёный — можно поставить, коралловый — возврат.
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded-md border border-danger-line bg-danger-soft p-3 text-sm text-danger-strong"
        >
          {error}
        </p>
      )}
      {structural && <EmptyState title="Раскладка недоступна" description={structural} />}
      {!structural && (
        <div
          ref={canvas}
          data-slot="home-canvas"
          data-editing={editing}
          data-desktop={desktop}
          className={cn('relative rounded-lg', desktop ? 'w-full' : 'space-y-4')}
          style={
            desktop
              ? {
                  height:
                    'calc(' +
                    height +
                    ' * (var(--home-grid-row) + var(--home-grid-gap)) - var(--home-grid-gap))',
                }
              : undefined
          }
        >
          {activity && desktop && (
            <div
              ref={placeholder}
              aria-hidden
              data-home-drop-preview=""
              className="pointer-events-none absolute rounded-lg border-2 border-dashed border-brand bg-brand-soft shadow-lg"
              style={positionStyle(activity.preview.find((item) => item.id === activity.id)!)}
            ></div>
          )}
          {activity?.kind === 'resize' && desktop && (
            <div
              ref={resizeFrame}
              aria-hidden
              data-home-resize-frame=""
              className="pointer-events-none absolute z-raised rounded-lg border-2 border-brand"
              style={positionStyle(activity.origin)}
            >
              <span
                ref={sizeLabel}
                data-home-size-label=""
                className="absolute right-2 bottom-2 rounded-sm bg-card px-2 py-1 text-xs font-semibold text-fg"
              >
                {activity.origin.columns} × {activity.origin.rows}
              </span>
            </div>
          )}
          {ordered.map((item, index) => {
            const widget = widgets.find((widget) => widget.id === item.id);
            if (!widget) return null;
            const grabbed = activity?.id === item.id;
            const dragHandleProps: ButtonHTMLAttributes<HTMLButtonElement> = {
              type: 'button',
              'aria-label': widget.title + ': переместить',
              'aria-describedby': helpId,
              'aria-pressed': grabbed,
              disabled: !focusable,
              onPointerDown: (event) => begin(item.id, 'move', event),
              onKeyDown: (event) => keyboard(item.id, event),
            };
            const removeAction = showHandles ? (
              <Tooltip content="Убрать виджет">
                <IconButton
                  type="button"
                  size="sm"
                  aria-label={widget.title + ': убрать'}
                  data-home-edit-control=""
                  disabled={!focusable || !!activity}
                  onClick={() => remove(item.id)}
                >
                  <X />
                </IconButton>
              </Tooltip>
            ) : null;
            const dragHandleRef = (node: HTMLButtonElement | null) => {
              if (node) handles.current.set(item.id, node);
              else handles.current.delete(item.id);
            };
            const controls: HomeWidgetControls = {
              ...api,
              layout:
                activity?.kind === 'resize'
                  ? shown.map((value) => (value.id === activity.id ? activity.origin : value))
                  : shown,
              grabbed,
              dragHandleProps,
              removeAction,
              dragHandleRef,
            };
            return (
              <div
                key={item.id}
                data-home-motion-frame=""
                className={cn('min-w-0', desktop ? 'absolute' : 'relative', grabbed ? 'z-dock' : 'z-raised')}
                style={
                  desktop
                    ? positionStyle(
                        grabbed && activity && activity.kind !== 'keyboard' ? activity.origin : item,
                      )
                    : undefined
                }
              >
                <article
                  data-home-widget={item.id}
                  data-home-column={item.column}
                  data-home-row={item.row}
                  data-home-columns={item.columns}
                  data-home-rows={item.rows}
                  data-home-grabbed={grabbed || undefined}
                  aria-label={widget.title}
                  onPointerDownCapture={(event) => {
                    if (editing && desktop && canEdit && !activity && freeWidgetBody(event))
                      begin(item.id, 'move', event);
                  }}
                  className="relative size-full min-w-0 rounded-lg"
                >
                  {widget.render ? (
                    <HomeRenderSlot render={widget.render} controls={controls} pointerKey={pointerKey} />
                  ) : (
                    <div className="flex h-full min-h-40 flex-col overflow-hidden rounded-lg border border-line bg-card">
                      <header className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-3">
                        <span aria-hidden className="shrink-0 text-fg-muted">
                          {widget.icon ?? <GripVertical className="size-4" />}
                        </span>
                        <h3 className="min-w-0 flex-1 type-subheading">
                          {showHandles ? (
                            <button
                              {...dragHandleProps}
                              ref={dragHandleRef}
                              data-home-drag-handle=""
                              className="flex w-full touch-none items-center gap-2 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                            >
                              {widget.title}
                            </button>
                          ) : (
                            widget.title
                          )}
                        </h3>
                        {removeAction}
                      </header>
                      <div className="min-h-0 flex-1 overflow-auto p-4">{widget.content}</div>
                      {widget.footer && (
                        <footer className="shrink-0 border-t border-line px-4 py-2">{widget.footer}</footer>
                      )}
                    </div>
                  )}
                  {showHandles && !desktop && (
                    <div className="mt-2 flex justify-end gap-1">
                      <IconButton
                        size="sm"
                        aria-label={widget.title + ': выше'}
                        data-home-edit-control=""
                        disabled={!focusable || index === 0 || !!activity}
                        onClick={() => {
                          prepareMotion();
                          setDraft(reorderHomeLayout(current, item.id, -1));
                          announce(widget.title + ' перемещён выше.');
                        }}
                      >
                        <ArrowUp />
                      </IconButton>
                      <IconButton
                        size="sm"
                        aria-label={widget.title + ': ниже'}
                        data-home-edit-control=""
                        disabled={!focusable || index === ordered.length - 1 || !!activity}
                        onClick={() => {
                          prepareMotion();
                          setDraft(reorderHomeLayout(current, item.id, 1));
                          announce(widget.title + ' перемещён ниже.');
                        }}
                      >
                        <ArrowDown />
                      </IconButton>
                    </div>
                  )}
                  {showHandles && desktop && (
                    <Tooltip content="Изменить размер">
                      <IconButton
                        type="button"
                        size="sm"
                        aria-label={widget.title + ': изменить размер'}
                        data-home-edit-control=""
                        aria-describedby={helpId}
                        onKeyDown={(event) => keyboard(item.id, event)}
                        className="absolute right-1 bottom-1 touch-none"
                        disabled={!focusable || (!!activity && activity.id !== item.id)}
                        onPointerDown={(event) => begin(item.id, 'resize', event)}
                      >
                        <MoveDiagonal />
                      </IconButton>
                    </Tooltip>
                  )}
                </article>
              </div>
            );
          })}
          {!ordered.length && (
            <EmptyState title="Добавь первый виджет" description="Собери свой обзор из доступных виджетов." />
          )}
        </div>
      )}
    </section>
  );
}
